import { useState } from "react";
import {
    TextField,
    usePermissions,
    useResourceContext,
    NumberField,
    FunctionField,
    useTranslate,
    useRecordContext,
    TabbedShowLayout,
    Tab,
    useGetOne,
    useNotify,
    ReferenceInput,
    required,
    useLocaleState,
} from "react-admin";
import { Button } from "@mui/material";
import { queryToAttribute } from "shared/src/utils/decodeQuery";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { ReferenceManyFieldWithLinkSupport } from "../components/ReferenceManyFieldWithLinkSupport";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";
import { SimpleFormWithPermissions } from "../components/SimpleFormWithPermissions";
import { RaDeleteButton } from "shared/src/components/buttons/RaDeleteButton";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { RaEditView } from "../components/views/RaEditView";
import { CreateSkuModal } from "./components/CreateSkusModal";
import { EditSkuModal } from "./components/EditSkuModal";
import { RaAutocompleteInput } from "../components/inputs/Ra/RaAutocompleteInput";
import { RaNumberInput } from "../components/inputs/Ra/RaNumberInput";
import { RaDateInput } from "../components/inputs/Ra/RaDateInput";
import { RaTextInput } from "../components/inputs/Ra/RaTextInput";

export const ArrivalEdit = (props) => {
    const resource = useResourceContext();
    const attribute = queryToAttribute();
    const [createSkuModalOpen, setCreateSkuModalOpen] = useState(false);
    const [editSkuModalOpen, setEditSkuModalOpen] = useState(false);
    const [skuRecord, setSkuRecord] = useState(null);
    const { isLoading, permissions } = usePermissions();

    const handleModalClose = () => {
        setCreateSkuModalOpen(false);
        setEditSkuModalOpen(false);
        setSkuRecord(null);
    };

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource, attribute })) {
        return <AccessRightsError />;
    }
    return (
        <RaEditView
            transform={({ product, supplier, currentPrice, ...rest }) => ({
                ...rest,
            })}
            actions={<ToolbarWithHistory back={true} show={true} />}
            {...props}
        >
            <CreateSkuModal
                open={createSkuModalOpen}
                onClose={handleModalClose}
                setCreateSkuModalOpen={setCreateSkuModalOpen}
            />
            <EditSkuModal
                open={editSkuModalOpen}
                onClose={handleModalClose}
                skuRecord={skuRecord}
                setEditSkuModalOpen={setEditSkuModalOpen}
            />
            <ArrivalEditForm>
                <SkuTable
                    setCreateSkuModalOpen={setCreateSkuModalOpen}
                    setEditSkuModalOpen={setEditSkuModalOpen}
                    setSkuRecord={setSkuRecord}
                />
            </ArrivalEditForm>
        </RaEditView>
    );
};

const ArrivalEditForm = ({ children }) => {
    const record = useRecordContext();
    const [locale] = useLocaleState();
    const { data: supplierData } = useGetOne(
        "suppliers",
        {
            id: record?.supplier?.id,
        },
        {
            enabled: !!record?.supplier?.id,
        }
    );
    const { data: productData } = useGetOne(
        "products",
        {
            id: record?.product?.id,
        },
        {
            enabled: !!record?.product?.id,
        }
    );
    return (
        <SimpleFormWithPermissions
            record={{ ...record, product: productData, supplier: supplierData }}
        >
            <ReferenceInput
                source="product.id"
                reference="products"
                sort={{ field: "name", order: "ASC" }}
            >
                <RaAutocompleteInput
                    label="equip.product"
                    validate={[required()]}
                    fullWidth
                    field="name"
                    optionText={({ name }) => name?.[locale] || ""}
                    inputText={(choice) => choice?.name?.[locale] || ""}
                    disabled
                />
            </ReferenceInput>
            <ReferenceInput
                source="supplier.id"
                reference="suppliers"
                sort={{ field: "name", order: "ASC" }}
            >
                <RaAutocompleteInput label="equip.supplier" fullWidth field="name" disabled />
            </ReferenceInput>
            <RaNumberInput label="equip.purchasePrice" source="purchasePrice" required />
            <RaTextInput label="equip.purchaseOrderNumber" source="purchaseOrderNumber" required />
            <RaDateInput label="equip.orderDate" source="orderDate" required />
            <RaDateInput label="equip.deliveryDate" source="deliveryDate" required />
            {productData?.isMassProduct && (
                <>
                    <RaNumberInput label="equip.deliveredOk" source="quantity" required />
                    <RaNumberInput label="equip.defectQuantity" source="defectQuantity" required />
                </>
            )}
            <RaTextInput label="equip.comment" source="comment" multiline rows={3} />
            <div>{children}</div>
        </SimpleFormWithPermissions>
    );
};

const SkuTable = ({ setCreateSkuModalOpen, setEditSkuModalOpen, setSkuRecord }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const record = useRecordContext();
    const { data: productData, isLoading: productIsLoading } = useGetOne(
        "products",
        {
            id: record?.product?.id,
        },
        {
            onError: (error) => {
                handleHttpError(error, notify);
            },
            enabled: !!record?.product?.id,
        }
    );

    const handleAddSku = () => {
        setCreateSkuModalOpen(true);
        setSkuRecord(null);
    };

    if (productIsLoading) {
        return <></>;
    }

    return productData?.isMassProduct ? (
        <></>
    ) : (
        <TabbedShowLayout sx={{ mb: 5 }}>
            <Tab label="equip.skus" title={translate("equip.skus")}>
                <Button sx={{ mt: 1 }} variant="outlined" color="primary" onClick={handleAddSku}>
                    + {translate("equip.addSkus")}
                </Button>
                <ReferenceManyFieldWithLinkSupport
                    reference="skus"
                    target="arrival"
                    addLabel={false}
                >
                    <DatagridWithHistory sx={{ marginTop: 1 }} bulkActionButtons={false}>
                        <TextField label="equip.labelNumber" source="labelNumber" />
                        <TextField label="equip.manufacturer" source="manufacturer.name" />
                        <TextField label="equip.supplier" source="supplier.name" />
                        <NumberField
                            label="equip.purchasePrice"
                            source=""
                            defaultValue={record?.purchasePrice}
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                        />
                        <NumberField
                            label="equip.currentPrice"
                            source="currentPrice"
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                            sortable={false}
                        />
                        <NumberField
                            label="equip.weight"
                            source="weight"
                            options={{
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                                style: "unit",
                                unit: "kilogram",
                            }}
                        />
                        <FunctionField
                            label="equip.status"
                            render={(record) => translate(`equip.sku_status_${record.status}`)}
                        />
                        <TextField label="equip.serialNumber" source="serialNumber" />
                        <EditSkuButton
                            setEditSkuModalOpen={setEditSkuModalOpen}
                            setSkuRecord={setSkuRecord}
                            redirect={false}
                        />
                        <RaDeleteButton redirect={false} />
                    </DatagridWithHistory>
                </ReferenceManyFieldWithLinkSupport>
            </Tab>
        </TabbedShowLayout>
    );
};

const EditSkuButton = ({ setEditSkuModalOpen, setSkuRecord }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setEditSkuModalOpen(true);
        setSkuRecord(record);
    };

    return (
        <Button type="button" variant="text" color="primary" onClick={handleClick}>
            {translate("ra.action.edit")}
        </Button>
    );
};
